import * as THREE from 'three';

class MeshMover {
  /**
   * Smoothly move a mesh to a target position with optional delay
   * @param {THREE.Object3D} mesh - The mesh to move
   * @param {THREE.Vector3} targetPosition - The destination position
   * @param {Object} options - Configuration options for the movement
   * @param {number} [options.duration=1] - Duration of the movement in seconds
   * @param {number} [options.delay=0] - Delay before starting the movement in seconds
   * @param {function} [options.easingFunction] - Optional custom easing function
   * @returns {Promise} A promise that resolves when the animation is complete
   */
  static smoothMove(mesh, targetPosition, options = {}) {
    const {
      duration = 1,
      delay = 0,
      easingFunction = null
    } = options;

    const startPosition = mesh.position.clone();
    
    return new Promise((resolve) => {
      // Apply delay using setTimeout
      const delayTimeout = setTimeout(() => {
        const startTime = performance.now();

        function animate(currentTime) {
          const elapsedTime = (currentTime - startTime) / 1000; // Convert to seconds
          const progress = Math.min(elapsedTime / duration, 1);

          // Use custom easing or default to smooth interpolation
          const easedProgress = easingFunction 
            ? easingFunction(progress) 
            : MeshMover.defaultEasing(progress);

          // Interpolate between start and target positions
          mesh.position.lerpVectors(startPosition, targetPosition, easedProgress);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // Ensure the mesh ends exactly at the target position
            mesh.position.copy(targetPosition);
            resolve();
          }
        }

        // Start the animation
        requestAnimationFrame(animate);
      }, delay * 1000); // Convert delay to milliseconds
    });
  }

  /**
   * Default easing function (smooth start and end)
   * @param {number} t - Progress from 0 to 1
   * @returns {number} Eased progress
   */
  static defaultEasing(t) {
    // Smooth interpolation (cubic ease in-out)
    return t < 0.5 
      ? 4 * t * t * t 
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /**
   * Collection of predefined easing functions
   */
  static easings = {
    linear: (t) => t,
    easeInQuad: (t) => t * t,
    easeOutQuad: (t) => t * (2 - t),
    easeInOutQuad: (t) => 
      t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
    easeInCubic: (t) => t * t * t,
    easeOutCubic: (t) => --t * t * t + 1,
    easeInOutCubic: (t) => 
      t < 0.5 
        ? 4 * t * t * t 
        : 1 - Math.pow(-2 * t + 2, 3) / 2
  };
}

export default MeshMover;